import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Clock, DollarSign, FolderOpen, TrendingUp, Plane, Download, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';

export const PayFlow = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: Clock,
      title: 'Easy Time Tracking',
      description: 'Clock in and out instantly, or enter shifts manually.',
    },
    {
      icon: FolderOpen,
      title: 'Multiple Projects',
      description: 'Assign different wages, overtime rules, per diem, and travel settings per project.',
    },
    {
      icon: DollarSign,
      title: 'Paycheck Estimator',
      description: 'Get accurate estimates for gross and net pay with built-in tax calculations.',
    },
    {
      icon: TrendingUp,
      title: 'OT & Pay Rules',
      description: 'Daily/weekly overtime, double-time, industry-specific rules.',
    },
    {
      icon: Plane,
      title: 'Per Diem & Travel Pay',
      description: 'Supports daily, in/out travel, and custom allowances.',
    },
    {
      icon: Download,
      title: 'Export Your Hours',
      description: 'Export CSV files for payroll, tracking, or personal records.',
    },
  ];

  const whatItDoes = [
    'One-tap clock in / clock out',
    'Track multiple projects with custom pay rules',
    'Estimate take-home pay, including federal, state, and FICA deductions',
    'Support for overtime, per diem, and travel pay',
    'Full shift history & pay period summaries',
  ];

  const screenshots = [
    { 
      name: 'Home Screen', 
      image: 'https://customer-assets.emergentagent.com/job_brand-layout-wizard/artifacts/3zp0gpb8_1000021814.jpg'
    },
    { 
      name: 'Project Setup', 
      image: 'https://customer-assets.emergentagent.com/job_brand-layout-wizard/artifacts/o14iqmwj_1000021816.jpg'
    },
    { 
      name: 'Manual Shift Entry', 
      image: 'https://customer-assets.emergentagent.com/job_brand-layout-wizard/artifacts/3bxwo1id_1000021818.jpg'
    },
    { 
      name: 'Paycheck Estimator', 
      image: 'https://customer-assets.emergentagent.com/job_brand-layout-wizard/artifacts/uzquhq0b_1000021820.jpg'
    },
    { 
      name: 'History / Export', 
      image: 'https://customer-assets.emergentagent.com/job_brand-layout-wizard/artifacts/7q1yw2h6_1000021822.jpg'
    },
  ];

  return (
    <>
      <Helmet>
        <title>PayFlow – Time Tracking & Paycheck Estimation App</title>
        <meta 
          name="description" 
          content="PayFlow is a time tracking and paycheck estimator app built for construction and industrial workers. Track hours, projects, overtime, per diem, and travel pay." 
        />
        <meta property="og:title" content="PayFlow – Time Tracking & Pay Estimator" />
        <meta property="og:description" content="PayFlow is a time tracking and paycheck estimator app built for construction and industrial workers. Track hours, projects, overtime, per diem, and travel pay." />
        <meta property="og:url" content="https://projectflowautomations.com/payflow" />
      </Helmet>

      <div className="min-h-screen bg-[#0D1115]">
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-[#0D1115] via-[#1A2733] to-[#1A2733] pt-20">
          {/* Animated background grid */}
          <div className="absolute inset-0 opacity-10">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `linear-gradient(to right, #30D5C8 1px, transparent 1px),
                                  linear-gradient(to bottom, #30D5C8 1px, transparent 1px)`,
                backgroundSize: '40px 40px',
              }}
            />
          </div>

          {/* Glow effect */}
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#30D5C8] rounded-full blur-[150px] opacity-10" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
            <div className="space-y-8">
              <div className="inline-block">
                <div className="flex items-center space-x-2 bg-[#1A2733]/50 backdrop-blur-sm border border-[#30D5C8]/30 rounded-full px-4 py-2 mb-8">
                  <div className="w-2 h-2 bg-[#30D5C8] rounded-full animate-pulse" />
                  <span className="text-[#30D5C8] text-sm font-['Montserrat'] font-semibold">
                    MOBILE APP
                  </span>
                </div>
              </div>

              <h1 className="font-['Montserrat'] font-extrabold text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-[#F5F7F8] leading-tight">
                PayFlow
              </h1>

              <p className="font-['Montserrat'] text-2xl sm:text-3xl md:text-4xl text-[#30D5C8] font-semibold max-w-4xl mx-auto">
                Track Your Hours. Know Your Pay. Every Time.
              </p>

              <p className="text-[#CBD3D9] text-lg sm:text-xl font-['Open_Sans'] max-w-3xl mx-auto">
                A simple yet powerful time tracking and paycheck estimation app designed for construction and field workers.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8">
                <Button
                  onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-[#30D5C8] text-[#0D1115] hover:bg-[#30D5C8]/90 font-['Montserrat'] font-bold text-lg px-8 py-6 rounded-lg"
                >
                  Learn More
                </Button>
                <Button
                  onClick={() => window.open('https://play.google.com/store/apps/details?id=com.projectflow.payflow', '_blank')}
                  variant="outline"
                  className="border-2 border-[#30D5C8] text-[#30D5C8] hover:bg-[#30D5C8]/10 font-['Montserrat'] font-bold text-lg px-8 py-6 rounded-lg"
                >
                  Download on Google Play
                </Button>
              </div>
            </div>
          </div>

          {/* Bottom gradient fade */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0D1115] to-transparent" />
        </section>

        {/* What PayFlow Does Section */}
        <section className="py-24 bg-[#0D1115]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-['Montserrat'] font-bold text-4xl sm:text-5xl text-[#F5F7F8] mb-6">
                What PayFlow <span className="text-[#30D5C8]">Does</span>
              </h2>
              <p className="text-[#CBD3D9] text-lg sm:text-xl font-['Open_Sans'] max-w-3xl mx-auto">
                Everything you need to track time and estimate your paycheck
              </p>
            </div>

            <div className="max-w-3xl mx-auto space-y-4">
              {whatItDoes.map((item, index) => (
                <Card
                  key={index}
                  className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 p-6 hover:border-[#30D5C8]/50 transition-all duration-300"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-[#30D5C8]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <Check className="text-[#30D5C8]" size={20} />
                    </div>
                    <p className="text-[#F5F7F8] text-lg font-['Open_Sans']">{item}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-24 bg-[#1A2733]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-['Montserrat'] font-bold text-4xl sm:text-5xl text-[#F5F7F8] mb-6">
                Powerful <span className="text-[#30D5C8]">Features</span>
              </h2>
              <p className="text-[#CBD3D9] text-lg sm:text-xl font-['Open_Sans'] max-w-3xl mx-auto">
                Built for the realities of construction and industrial work
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <Card
                  key={index}
                  className="bg-[#0D1115]/80 backdrop-blur-sm border-[#506273]/30 p-8 hover:border-[#30D5C8]/50 transition-all duration-300 card-hover group"
                >
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="w-16 h-16 bg-[#30D5C8]/10 rounded-xl flex items-center justify-center group-hover:bg-[#30D5C8]/20 transition-colors group-hover:scale-110 transition-transform">
                      <feature.icon className="text-[#30D5C8]" size={32} />
                    </div>
                    <h3 className="font-['Montserrat'] font-bold text-xl text-[#F5F7F8]">
                      {feature.title}
                    </h3>
                    <p className="text-[#CBD3D9] font-['Open_Sans']">
                      {feature.description}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Screenshots Section */}
        <section className="py-24 bg-[#0D1115]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-['Montserrat'] font-bold text-4xl sm:text-5xl text-[#F5F7F8] mb-6">
                See PayFlow in <span className="text-[#30D5C8]">Action</span>
              </h2>
              <p className="text-[#CBD3D9] text-lg sm:text-xl font-['Open_Sans'] max-w-3xl mx-auto">
                Real app screenshots from PayFlow users
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {screenshots.map((screenshot, index) => (
                <Card
                  key={index}
                  className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 p-6 hover:border-[#30D5C8]/50 transition-all duration-300 card-hover group"
                >
                  <div className="aspect-[9/16] bg-[#0D1115]/50 rounded-lg overflow-hidden mb-4">
                    <img 
                      src={screenshot.image} 
                      alt={screenshot.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                  <p className="text-[#F5F7F8] font-['Montserrat'] font-semibold text-center">
                    {screenshot.name}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Download Section */}
        <section className="py-24 bg-[#1A2733]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-[#0D1115]/50 backdrop-blur-sm border border-[#30D5C8]/30 rounded-2xl p-12">
              <h2 className="font-['Montserrat'] font-bold text-3xl sm:text-4xl text-[#F5F7F8] mb-6">
                Download PayFlow on Google Play
              </h2>
              <p className="text-[#CBD3D9] text-lg font-['Open_Sans'] mb-8">
                Available now for Android devices
              </p>
              <Button
                onClick={() => window.open('https://play.google.com/store/apps/details?id=com.projectflow.payflow', '_blank')}
                className="bg-[#30D5C8] text-[#0D1115] hover:bg-[#30D5C8]/90 font-['Montserrat'] font-bold text-lg px-10 py-7 rounded-lg"
              >
                <Download className="mr-2" size={24} />
                Get PayFlow
              </Button>
              <p className="text-[#506273] text-sm font-['Open_Sans'] mt-6">
                Link will be updated after app publication
              </p>
            </div>
          </div>
        </section>

        {/* About PayFlow Section */}
        <section className="py-24 bg-[#0D1115]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="font-['Montserrat'] font-bold text-4xl sm:text-5xl text-[#F5F7F8] mb-8">
                About <span className="text-[#30D5C8]">PayFlow</span>
              </h2>
              <div className="bg-[#1A2733]/50 backdrop-blur-sm border border-[#506273]/30 rounded-2xl p-8 sm:p-12">
                <p className="text-[#CBD3D9] text-lg sm:text-xl font-['Open_Sans'] leading-relaxed">
                  PayFlow was developed by ProjectFlow Automations to simplify timekeeping and paycheck clarity for industrial and construction workers. Built for reliability, simplicity, and accuracy.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Back to Home */}
        <section className="py-12 bg-[#1A2733]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <a
              href="/"
              className="inline-block bg-[#506273] text-[#F5F7F8] hover:bg-[#506273]/80 font-['Montserrat'] font-bold text-lg px-8 py-4 rounded-lg transition-colors"
            >
              Back to Home
            </a>
          </div>
        </section>
      </div>
    </>
  );
};
